import { useCallback, useEffect, useMemo, useState } from "react";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { useEffectAfterMount } from "@/Common/CustomHooks/useEffectAfterMount";
import { usePartialState } from "@/Common/CustomHooks/usePartialState";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Patient } from "@/Common/Models/Patient/Patient";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { DirectoryFaxUpdateOptions } from "@/ContactDirectory/Models/DirectoryFaxUpdateOptions";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { FaxDirectorySearchResultItemMapper } from "../Api/Mapper/FaxDirectorySearchResultItemMapper";
import { useDefaultDirectoryCountryAndState } from "../Hooks/useDefaultDirectoryCountryAndState";
import { useDefaultFaxDirectoryContacts } from "../Hooks/useDefaultFaxDirectoryContacts";
import { DirectoryFaxNumber } from "../Models/DirectoryFaxNumber";
import { DirectoryHealthCareProvider } from "../Models/DirectoryHealthCareProvider";
import { IDirectoryEntity } from "../Models/IDirectoryEntity";
import { IPatientPreferredDirectoryContactsContext, PatientPreferredDirectoryContactsContext } from "../Models/PatientPreferredDirectoryContactsContext";
import { FaxDirectorySearchComponent } from "./Search/FaxDirectorySearchComponent";
import { FaxDirectorySearchResultItem } from "./Search/FaxDirectorySearchResultItem";
import { FaxDirectoryManualEntry } from "./Selection/FaxDirectoryManualEntry";
import { FaxDirectorySelectedEntityComponent } from "./Selection/FaxDirectorySelectedEntityComponent";
import { DirectoryPopupController } from "./DirectoryPopupController";

import styles from "./FaxDirectoryForm.module.scss";

interface IFaxDirectorySelectorComponentProps {
    patientContactId?: string;
    isFaxValid: (isValid: boolean) => void;
    onFaxUpdate: (options: DirectoryFaxUpdateOptions) => void
    patient?: Patient;
    enableUpdateExternalConsultantFaxNumberCheckbox?: boolean;
    defaultSelectedRecipient?: DirectorySelectableEntity<IDirectoryEntity> | string | null;
    defaultSelectedFaxNumber?: string;
}

interface ISelectedFaxInformation {
    selectedEntity: FaxDirectorySearchResultItem | null;
    entityType: DirectoryEntityType | null;
    selectedFaxNumberId: string;
    selectedFaxNumber: string;
    countryId: string;
    name: string;
}

function FaxDirectorySelectorComponent(props: Readonly<IFaxDirectorySelectorComponentProps>): JSX.Element {
    const { preferredPharmacy, externalConsultants } = useDefaultFaxDirectoryContacts(props.patientContactId, props.patient);
    const context: IPatientPreferredDirectoryContactsContext = {
        patientContactId: props.patientContactId,
        preferredPharmacy,
        externalConsultants
    };

    return (
        <PatientPreferredDirectoryContactsContext.Provider value={context}>
            <FaxDirectorySelectorComponentWithoutContext
                {...props}
            />
        </PatientPreferredDirectoryContactsContext.Provider>
    );
}

function FaxDirectorySelectorComponentWithoutContext(props: Readonly<IFaxDirectorySelectorComponentProps>): JSX.Element {
    const { getText } = useTranslation();
    const { defaultCountry: defaultPharmacyCountry } = useDefaultDirectoryCountryAndState(props.patientContactId, true);

    const [showFaxDirectorySelector, setShowFaxDirectorySelector] = useState<boolean>(false);

    const selectedRecipientIsManuallyEntered: boolean = typeof props.defaultSelectedRecipient === "string";
    const [selectedFaxInfo, setSelectedFaxInfo] = usePartialState<ISelectedFaxInformation>(() => {
        let selectedRecipientFaxInfo: ISelectedFaxInformation = { selectedEntity: null, selectedFaxNumber: null, selectedFaxNumberId: null, countryId: "", name: "", entityType: null };
        if (!selectedRecipientIsManuallyEntered) {
            const selectedRecipient = props.defaultSelectedRecipient as DirectorySelectableEntity<IDirectoryEntity>;
            const defaultFaxNumber: DirectoryFaxNumber | null = selectedRecipient?.model?.getDefaultFaxNumberToSelect?.();
            selectedRecipientFaxInfo = {
                selectedEntity: props.defaultSelectedRecipient ? new FaxDirectorySearchResultItem({ entity: selectedRecipient?.model }) : null,
                selectedFaxNumber: defaultFaxNumber?.number,
                selectedFaxNumberId: defaultFaxNumber?.id,
                countryId: defaultFaxNumber?.countryId,
                name: selectedRecipient?.model?.getDisplayName(),
                entityType: selectedRecipient?.model?.type
            };
        }
        else {
            selectedRecipientFaxInfo.name = props.defaultSelectedRecipient as string;
            selectedRecipientFaxInfo.selectedFaxNumber = props.defaultSelectedFaxNumber;
        }

        return selectedRecipientFaxInfo;
    });

    const [isManualNameValid, setIsManualNameValid] = useState<boolean>(false);
    const [selectedExternalConsultant, setSelectedExternalConsultant] = useState<DirectoryHealthCareProvider>(null);
    const [isPreferredChecked, setIsPreferredChecked] = useState<boolean>(false);

    useEffect(
        () => {
            const isManuallyEnteredRecipientValid = selectedRecipientIsManuallyEntered && !!props.defaultSelectedRecipient;
            const isNameValid = !!selectedFaxInfo.selectedEntity || isManualNameValid || isManuallyEnteredRecipientValid;
            const isFaxValid = PhoneNumberHelper.validate(selectedFaxInfo.selectedFaxNumber, selectedFaxInfo.countryId);

            props.isFaxValid(isNameValid && isFaxValid);
        },
        [isManualNameValid, selectedFaxInfo.selectedEntity, selectedFaxInfo.selectedFaxNumber, selectedFaxInfo.countryId, props.defaultSelectedRecipient]
    );

    useEffectAfterMount(
        () => {
            props.onFaxUpdate({
                selectedFaxNumberId: selectedFaxInfo.selectedFaxNumberId,
                faxNumber: selectedFaxInfo.selectedFaxNumber,
                description: selectedFaxInfo.name,
                countryCode: cacheStore.CountryCache.firstOrDefault(p => p.Id === selectedFaxInfo.countryId)?.Alpha2Code,
                shouldUpdatePatientLink: isPreferredChecked,
                selectedEntity: selectedFaxInfo.selectedEntity?.preferredPharmacy?.getDirectoryPharmacy() ?? selectedFaxInfo.selectedEntity?.entity,
                entityType: selectedFaxInfo.entityType
            });
        },
        [selectedFaxInfo, isPreferredChecked]
    );

    const selectedEntityHasFaxInfo = useMemo(
        () => FaxDirectorySearchResultItemMapper.toDirectorySelectableEntity(selectedFaxInfo.selectedEntity)?.faxInfos?.any() ?? false,
        [selectedFaxInfo.selectedEntity]
    );

    const showManualFaxEntry = useMemo(
        () => !selectedFaxInfo.selectedEntity
            || !selectedEntityHasFaxInfo
            || !PhoneNumberHelper.validate(selectedFaxInfo.selectedFaxNumber, selectedFaxInfo.countryId),
        [
            selectedFaxInfo.selectedEntity,
            selectedEntityHasFaxInfo,
            selectedFaxInfo.selectedFaxNumber,
            selectedFaxInfo.countryId
        ]
    );

    function selectEntity(selection: FaxDirectorySearchResultItem): void {
        const selectableEntity = FaxDirectorySearchResultItemMapper.toDirectorySelectableEntity(selection);
        let fax = selection.selectedFaxNumberId
            ? selectableEntity?.faxInfos?.find(p => p.faxNumber.id === selection.selectedFaxNumberId)?.faxNumber
            : selectableEntity?.faxInfos?.[0]?.faxNumber;

        setSelectedExternalConsultant(null);

        if (selectableEntity?.type === DirectoryEntityType.HealthcareProvider) {
            const model = selectableEntity.model as DirectoryHealthCareProvider;
            fax = model?.selectedExternalFaxNumber ?? fax;
            if (model.id && model.isExternalConsultant) {
                setSelectedExternalConsultant(model);
                setIsPreferredChecked(false);
            }
        }

        const shouldLeaveManualFaxEntry = !fax && showManualFaxEntry;

        const faxInfo: ISelectedFaxInformation = {
            selectedEntity: selection,
            selectedFaxNumberId: fax?.id,
            selectedFaxNumber: shouldLeaveManualFaxEntry ? selectedFaxInfo.selectedFaxNumber : fax?.number,
            countryId: shouldLeaveManualFaxEntry ? selectedFaxInfo.countryId : fax?.countryId,
            name: selectableEntity?.name,
            entityType: selectableEntity?.type
        };

        if (selection?.preferredPharmacy?.CustomPharmacy)
            faxInfo.countryId = defaultPharmacyCountry.Id;

        setSelectedFaxInfo(faxInfo);
    }

    const closeSelectedEntity = useCallback(
        (): void => {
            setSelectedFaxInfo({
                selectedEntity: null,
                selectedFaxNumberId: null,
                selectedFaxNumber: showManualFaxEntry ? selectedFaxInfo.selectedFaxNumber : "",
                countryId: showManualFaxEntry ? selectedFaxInfo.countryId : "",
                name: ""
            });
            setSelectedExternalConsultant(null);
            setIsManualNameValid(false);
        },
        [selectedFaxInfo, showManualFaxEntry]
    );

    const shouldDisplayPreferredExternalConsultantCheckBox = (): boolean =>
        props.enableUpdateExternalConsultantFaxNumberCheckbox
        && !!selectedExternalConsultant
        && selectedExternalConsultant.isExternalConsultant
        && selectedExternalConsultant.selectedExternalFaxNumber?.id !== selectedFaxInfo.selectedFaxNumberId;

    return (
        <div>
            <div>
                <a
                    style={{ color: "#0070d2" }}
                    className={styles.addToDirectoryLink}
                    onClick={() => setShowFaxDirectorySelector(true)}
                >
                    {getText("AddToDirectoryLabel")}
                </a>
                {showFaxDirectorySelector && (
                    <DirectoryPopupController
                        onSave={(entity: IDirectoryEntity) => {
                            selectEntity(new FaxDirectorySearchResultItem({ entity }));
                            setShowFaxDirectorySelector(false);
                        }}
                        onClose={() => setShowFaxDirectorySelector(false)}
                        onEntityUseInstead={entity => {
                            selectEntity(new FaxDirectorySearchResultItem({ entity }));
                            setShowFaxDirectorySelector(false);
                        }}
                        onModify={entity => {
                            selectEntity(new FaxDirectorySearchResultItem({ entity }));
                            setShowFaxDirectorySelector(false);
                        }}
                    />
                )}
            </div>

            <div className={styles.selectedEntity}>
                <label className="bold">{getText("RecipientNameLabel")}</label>
                <FaxDirectorySearchComponent
                    patientContactId={props.patientContactId}
                    showPreferredPharmacy
                    showExternalConsultants
                    onItemSelected={(selection: FaxDirectorySearchResultItem) => selectEntity(selection)}
                    isValid={!IsNullOrWhiteSpace(selectedFaxInfo.selectedEntity?.getDisplayName()) || !IsNullOrWhiteSpace(selectedFaxInfo.name)}
                    onSearchTextChanged={(name, isValid) => {
                        setSelectedFaxInfo({ name });
                        setIsManualNameValid(isValid);
                    }}
                    entityTypes={[]}
                    dataCy="contactDirectory-recipientSearch"
                    allowExpandingGeographicSearch
                    searchText={(selectedRecipientIsManuallyEntered ? props.defaultSelectedRecipient as string : null)}
                />
            </div>

            {selectedFaxInfo.selectedEntity &&
                (
                    <div className={styles.selectedEntity}>
                        <FaxDirectorySelectedEntityComponent
                            entity={FaxDirectorySearchResultItemMapper.toDirectorySelectableEntity(selectedFaxInfo.selectedEntity)}
                            selectedNumberId={selectedFaxInfo.selectedFaxNumberId}
                            setSelectedNumberId={id => {
                                const fax = FaxDirectorySearchResultItemMapper.toDirectorySelectableEntity(selectedFaxInfo.selectedEntity)
                                    ?.faxInfos
                                    ?.firstOrDefault(p => p.faxNumber?.id === id)
                                    ?.faxNumber;

                                if (shouldDisplayPreferredExternalConsultantCheckBox())
                                    setIsPreferredChecked(false);

                                setSelectedFaxInfo({
                                    selectedFaxNumberId: id,
                                    selectedFaxNumber: fax?.number,
                                    countryId: fax?.countryId,
                                    name: selectedFaxInfo.selectedEntity.getDisplayName()
                                });
                            }}
                            close={closeSelectedEntity}
                            patientContactId={props.patientContactId}
                        />
                    </div>
                )
            }
            {showManualFaxEntry && (
                <div>
                    <FaxDirectoryManualEntry
                        faxNumber={selectedFaxInfo?.selectedFaxNumber ?? ""}
                        setSelectedFaxNumber={(faxNumber, countryId) => {
                            setSelectedFaxInfo({
                                selectedFaxNumber: faxNumber,
                                countryId: countryId,
                                selectedFaxNumberId: null
                            });
                        }}
                        patientContactId={props.patientContactId}
                        isForPharmacy={selectedFaxInfo.entityType === DirectoryEntityType.Pharmacy}
                    />
                </div>
            )}
            {shouldDisplayPreferredExternalConsultantCheckBox() && (
                <CheckboxControl
                    labelClassName="margin-sm-top"
                    checked={isPreferredChecked}
                    onChange={() => setIsPreferredChecked(!isPreferredChecked)}
                    label={getText("UpdateExternalConsultantFaxNumberLabel")}
                />
            )}
        </div>
    );
}

export { FaxDirectorySelectorComponent, IFaxDirectorySelectorComponentProps };
